import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const officeTips = [
  {
    id: "identify-risks",
    icon: "ri-search-eye-line",
    title: "Promptly Identify and Remove All Potential Fire Risks",
    content:
      "Sometimes, office fires are started by simple things like neglected garbage left around and electrical faults on equipment used by the staff. The first step to preventing such issues is making sure that the entire office staff are trained on how to immediately identify potential fire hazards at their work stations. They should know about the common fire hazards to avoid like overloading circuits, leaving hazardous machines unattended and using faulty electrical appliances. Apart from just knowing how to identify these threats, office staff should also be enlightened on the appropriate measures to take so as to eliminate those risks. Keeping the office safe from fire threats should be the responsibility of every staff member.",
  },
  {
    id: "response-procedures",
    icon: "ri-route-line",
    title: "Educate the Staff on Proper Fire Response Procedures",
    content:
      "Whenever a fire breaks out in the office, the employees in the building usually have the best chance at stopping it before it ravages the entire property. Considering the fear and confusion that fire emergencies spark, it is vital that the staffs are enlightened on the right response procedures to such incidences. They should know how to safely evacuate the building, operate fire protection systems and secure valuable items from fire. Offering hands-on training to your office staff about proper fire response will not only save their lives but also protect belonging from serious damage during fires. With the training and right fire safety systems in the office, you will be able to safely handle all cases of fires whenever they occur without necessarily relying on fire services or rescue teams.",
  },
  {
    id: "inspections",
    icon: "ri-tools-line",
    title: "Conduct Regular Fire Inspections, Maintenance and Testing",
    content:
      "Defective fire protection systems can also spark fires in the office. Besides, some fire protection systems also run on batteries that require replacement. Normal tear and wear can also make some fire protection systems to fail, putting your office and staff in great danger during fire emergencies. Therefore, it is recommended that all the fire protection systems and fire safety products in the office are regularly inspected, maintained and tested in accordance with the fire safety regulations Singapore. Routine fire inspections, maintenance and testing should be conducted by Qualified Persons or licensed inspectors and clearly documented for review. Routine fire inspections and maintenance will also preserve the fire protection systems and office in good condition over time, hence, saving you from the huge expenses of frequent repairs.",
  },
  {
    id: "clear-exits",
    icon: "ri-door-open-line",
    title: "Keep All the Fire and Emergency Exits Clear",
    content:
      "Fire and emergency exits are rarely used but they should always remain clear for easy and immediate access whenever there is need. Blocked exits can prevent the safe evacuation of occupants during fire emergencies and may even result in fatalities. Conduct regular walkthrough inspections to ensure all exit routes are free from obstructions.",
  },
  {
    id: "act-on-reports",
    icon: "ri-alarm-warning-line",
    title: "Take Immediate Actions on All Reports of Potential Fire Hazards",
    content:
      "Apart from just identifying and removing potential fire hazards in the office, all the staff members should also report the issues to their superiors immediately. Whenever such reports are made, the management should always act swiftly to follow up on the issues and make sure that they are properly resolved. In case the threat is not eliminated as required, you should hire a fire safety professional to address it as soon as possible.",
  },
];

export default function OfficeFireSafetyPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Office Ezzogenics",
            description:
              "Essential office fire safety tips for businesses in Singapore, including identifying fire hazards, fire response training, routine inspections, keeping exits clear and responding to hazard reports.",
            publisher: { "@type": "Organization", name: "Ezzogenics" },
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-20 min-h-[380px] md:min-h-[460px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/61af8ffe-9182-4098-98d7-be6c4e3b8ce4_compressed_39e61b20-7bad-4c2a-b6cb-20a490edb8c1.webp"
              alt="Office fire safety ceiling with sprinkler exit lights and AC Singapore"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/50 to-foreground-950/20"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300 flex-wrap">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/resources" className="hover:text-background-50 transition-colors">Resources</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">Office Fire Safety</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              Commercial Fire Safety
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              Office Fire Safety <span className="text-primary-400">Singapore</span>
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed">
              Offices are among the main commercial spaces that stand the greatest risks of fire emergencies. Since the office holds some of the most valuable assets including people and equipment, your office requires the best fire protection measures.
            </p>
          </div>
        </section>

        {/* Intro */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            <div className="lg:col-span-2">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                The Risk
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Why Offices Are at High Risk
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                Offices are among the main commercial spaces that stand the greatest risks of fire emergencies. Since the office is the centre of most business activities, it usually holds some of the most valuable assets including people and equipment. As such, your office requires the best fire protection and safety measures that will ensure the safety of the staff and also minimize the risk of destruction to machines, documents and other belongings therein during an emergency.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed">
                To help secure the lives of your staff and assets from fire threats on the building, below are some important office fire safety tips to consider.
              </p>
            </div>
            <div className="bg-primary-500 rounded-2xl p-6 text-background-50 flex flex-col justify-between">
              <div>
                <i className="ri-building-2-line text-3xl mb-3 block"></i>
                <h3 className="font-heading text-lg font-bold mb-2">Total Fire Protection Solutions</h3>
                <p className="text-background-100 text-sm leading-relaxed mb-4">
                  Talk to Ezzogenics for QP consultancy services and total fire protection solutions for your office in Singapore.
                </p>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-50 text-primary-600 text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap mt-4"
              >
                Get a Consultation
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Tips */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                Best Practices
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Important Office Fire <span className="text-primary-500">Safety Tips</span>
              </h2>
            </div>
            <div className="flex flex-col gap-5">
              {officeTips.map((tip, i) => (
                <div key={tip.id} id={tip.id} className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
                  <div className="flex items-start gap-5">
                    <div className="flex flex-col items-center gap-2 flex-shrink-0">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100">
                        <i className={`${tip.icon} text-primary-600 text-xl`}></i>
                      </div>
                      <span className="text-xs font-bold text-foreground-400">0{i + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">
                        <a href={`#${tip.id}`}>{tip.title}</a>
                      </h3>
                      <p className="text-foreground-600 text-base leading-relaxed">{tip.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            Ensure Total Fire Safety in Your Office
          </h2>
          <p className="text-foreground-600 text-base max-w-xl mx-auto mb-8">
            Ensuring office fire safety entails a lot that may not be achieved in a day, week or month. For convenience, talk to Ezzogenics for QP consultancy services and total fire protection solutions Singapore.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Get QP Consultancy
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              Browse Fire Safety Products
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}